import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const ThemeContext = createContext(null);
export const useTheme = () => useContext(ThemeContext);

const KEY = "asl_theme"; // 'light' | 'dark' | 'system'

const getSystem = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";

function applyEffective(effective) {
  const root = document.documentElement;
  root.classList.toggle("dark", effective === "dark");
  root.style.colorScheme = effective;
}

export const ThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(() => {
    try { return localStorage.getItem(KEY) || "system"; } catch { return "system"; }
  });
  const [effective, setEffective] = useState(() => (mode === "system" ? getSystem() : mode));

  const setMode = useCallback((m) => {
    try { localStorage.setItem(KEY, m); } catch {}
    // Brief, property-scoped transition (respects prefers-reduced-motion via CSS)
    const root = document.documentElement;
    root.classList.add("theme-anim");
    window.clearTimeout(window.__aslThemeAnimT);
    window.__aslThemeAnimT = window.setTimeout(() => root.classList.remove("theme-anim"), 360);
    setModeState(m);
  }, []);

  useEffect(() => {
    const eff = mode === "system" ? getSystem() : mode;
    setEffective(eff);
    applyEffective(eff);
    if (mode === "system" && window.matchMedia) {
      const mq = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => { const e = getSystem(); setEffective(e); applyEffective(e); };
      mq.addEventListener ? mq.addEventListener("change", handler) : mq.addListener(handler);
      return () => { mq.removeEventListener ? mq.removeEventListener("change", handler) : mq.removeListener(handler); };
    }
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, effective, setMode }}>
      {children}
    </ThemeContext.Provider>
  );
};
